import { useState, useEffect, useRef, useDeferredValue, lazy, Suspense, useMemo } from "react";
import { TABS, GRIDS, RESPONSIVE, FORM_CONTROLS, TYPOGRAPHY, SPACING } from "@/design-system";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserContext } from "@/contexts/UserContext";
import { useLocalizedPracticeAreas } from "@/hooks/useLocalizedPracticeAreas";
// useTranslation removed - using useLocalizations instead
import { useLocalizations } from "@/hooks/useLocalizations";
import { getCountryDisplayName } from "@/hooks/useCountryName";
import i18n from "@/i18n/config";
import { LPages, LCommon } from "@/components/i18n";
import { useSEO } from "@/hooks/useSEO";
import { SEOHead, generateItemListSchema } from "@/components/seo/SEOHead";
import { useLocalizedLocations } from "@/hooks/useLocalizedLocations";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { UserCardSkeleton } from "@/components/skeletons/UserCardSkeleton";
import { MapPin, ChevronDown, Search, Globe, RotateCcw, List, Map as MapIcon } from "lucide-react";
import { db } from '@/lib/supabaseQuery';
import { User, UsersPracticeArea } from "@/types/database";
import { countries } from "@/data/countries";
import { UserCard } from "@/components/UserCard";
import { UserCardHorizontal } from "@/components/UserCardHorizontal";
import { isPremiumTier } from "@/constants/membershipTiers";
import { InquirySuggestions } from "@/components/InquirySuggestions";
import { useMyMembership } from "@/hooks/useMyMembership";
import { useUserRole } from "@/hooks/useUserRole";
import { VerifyCTA } from "@/components/experts/VerifyCTA";
import { logger } from "@/utils/logger";
import { cn } from "@/lib/utils";
import { StandardPagination } from "@/components/ui/standard-pagination";
import { SearchInput } from "@/components/admin/primitives/inputs/SearchInput";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getTopExpertByLocation } from "@/utils/expertPriorityQuery";
import { LocationPicker } from "@/components/shared/LocationPicker";
import { INTERNATIONAL_LOCATION_ID } from "@/constants/config";
import { useLocalizedExpertBios } from "@/hooks/useLocalizedExpertBios";

import { VirtualizedUserGrid } from "@/components/VirtualizedUserGrid";
import { useLocationNavigation } from "@/hooks/useLocationNavigation";
import { useAnalyticsTracking } from "@/hooks/useAnalyticsTracking";

// Lazy load the heavy map component
const GlobalMap = lazy(() => import("@/components/GlobalMap"));

const Experts = () => {
  const { user } = useAuth();
  const { locationSlug } = useParams();
  const { data: membership } = useMyMembership();
  const hasPaidMembership = !!membership;
  const { isSuperAdmin } = useUserRole();
  const { intentMode, isLoading: contextLoading } = useUserContext();
  const { locations } = useLocalizedLocations();
  const { lPages, lCommon } = useLocalizations();
  
  const { selectLocation, selectInternational, currentLocation } = useLocationNavigation();
  const { trackSearchSubmit } = useAnalyticsTracking();
  const searchTrackedRef = useRef<string>('');
  
  const seoData = useSEO({
    routeKey: 'experts',
    title: lPages('experts.seo.title', { defaultValue: 'Directory of Tax Professionals' }),
    description: lPages('experts.seo.description', { defaultValue: 'Find and connect with qualified tax and compliance professionals.' }),
    keywords: [lPages('experts.seo.keywords.experts', { defaultValue: 'Experts' }), lPages('experts.seo.keywords.consultants', { defaultValue: 'Consultants' }), lPages('experts.seo.keywords.professionals', { defaultValue: 'Professionals' }), lPages('experts.seo.keywords.advisors', { defaultValue: 'Advisors' }), lPages('experts.seo.keywords.specialists', { defaultValue: 'Specialists' })],
  });
  
  const [experts, setExperts] = useState<User[]>([]);
  const [practiceAreas, setPracticeAreas] = useState<UsersPracticeArea[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedPracticeAreas, setSelectedPracticeAreas] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  
  // Filter out the current user if they have no paid membership and not searching
  const displayExperts = useMemo(() => {
    if (user && !hasPaidMembership && !isSuperAdmin && !deferredSearchQuery) {
      return experts.filter(e => e.id !== user.id);
    }
    return experts;
  }, [experts, user, hasPaidMembership, isSuperAdmin, deferredSearchQuery]);
  // locationPickerOpen removed - now using LocationPicker component
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [hasLocalExperts, setHasLocalExperts] = useState(true);
  const isGlobalMode = selectedLocations.length === 0 
    || selectedLocations[0] === INTERNATIONAL_LOCATION_ID 
    || selectedLocations[0] === '0' 
    || intentMode === 'international';
  const ITEMS_PER_PAGE = isGlobalMode ? 36 : 38;
  // Map expects an ISO country code (e.g. "BR"); selectedLocations holds numeric location IDs.
  const selectedLocationId = selectedLocations.length === 1 ? selectedLocations[0] : null;
  const mapLocationCode = useMemo(() => {
    if (isGlobalMode || !selectedLocationId) return undefined;
    const iso = locations.find(l => l.id?.toString() === selectedLocationId)?.iso;
    return iso ? iso.toUpperCase() : undefined;
  }, [isGlobalMode, selectedLocationId, locations]);
  const mapKey = isGlobalMode ? 'global' : `loc-${selectedLocationId ?? 'global'}`;
  const USE_VIRTUAL_SCROLL = false; // Disabled — card heights vary too much for fixed-height virtualizer
  // Track if user has manually selected a location - their choice takes priority
  const hasUserSelectedLocation = useRef(false);

  // Initialize selected location - only use context location on location-specific routes
  // Global /experts route should always default to global mode
  useEffect(() => {
    // Skip if user has manually selected - their choice takes priority
    if (hasUserSelectedLocation.current) return;
    
    // Wait for context to finish loading
    if (contextLoading) return;
    
    // Use currentLocation from UserContext (set by DeterministicRouter) instead of useParams
    // Convert to string for comparison since INTERNATIONAL_LOCATION_ID is '0'
    const locationStr = currentLocation?.toString() || '';
    const isLocationSpecificRoute = locationStr && locationStr !== INTERNATIONAL_LOCATION_ID && locationStr !== '0';
    
    logger.debug('[Experts] Location sync check', { 
      currentLocation, 
      locationStr,
      isLocationSpecificRoute, 
      intentMode,
      contextLoading,
      locationSlug 
    });
    
    if (isLocationSpecificRoute && intentMode !== 'international') {
      logger.debug('[Experts] Setting location filter:', locationStr);
      setSelectedLocations([locationStr]);
    } else {
      // Global route: always start in global mode
      logger.debug('[Experts] Setting global mode');
      setSelectedLocations([]);
    }
  }, [currentLocation, intentMode, contextLoading, locationSlug]);

  // Load practice areas
  useEffect(() => {
    const fetchPracticeAreas = async () => {
      const { data, error } = await db.raw
        .from('users_practice_areas')
        .select('practice_area_id, practice_area')
        .order('practice_area', { ascending: true });

      if (error) {
        // Silent fail
      } else {
        setPracticeAreas(data || []);
      }
    };

    fetchPracticeAreas();
  }, []);

  useEffect(() => {
   if (contextLoading) return;

    // Guard: skip fetch if location context indicates a specific location
    // but selectedLocations hasn't synced yet (race condition on re-mount)
    if (!hasUserSelectedLocation.current && currentLocation && currentLocation !== INTERNATIONAL_LOCATION_ID && currentLocation !== '0' && selectedLocations.length === 0) {
      return; // Wait for location sync effect to set selectedLocations
    }

    const fetchExperts = async () => {
      setLoading(true);
      setLoadError(false);
      setHasLocalExperts(true); // Reset to avoid stale false-positive from previous fetch
      
      // Use selectedLocations as the source of truth for filtering
      // This ensures the filter state is synced before fetching
      const effectiveLocation = selectedLocations.length > 0 ? selectedLocations[0] : null;
      // isGlobalMode is derived at component level for ITEMS_PER_PAGE
      
      logger.debug('[Experts] Fetching experts', { effectiveLocation, isGlobalMode, selectedLocations, intentMode });
      
      try {
        if (isGlobalMode && !searchQuery.trim()) {
          // GLOBAL MODE: Use optimized database function (1 query instead of 245+)
          const { data: globalExperts, error } = await db.raw
            .rpc('get_top_experts_by_location', {
              p_limit: ITEMS_PER_PAGE,
              p_offset: (currentPage - 1) * ITEMS_PER_PAGE
            });

          if (error) {
            logger.error('Error fetching global experts:', error);
            setLoadError(true);
            setExperts([]);
            setTotalCount(0);
            setLoading(false);
            return;
          }

          // Get total count for pagination using count RPC
          const { data: countData } = await db.raw
            .rpc('count_directory_experts');
          const count = typeof countData === 'number' ? countData : 0;
          
          setExperts((globalExperts || []) as User[]);
          setTotalCount(count || globalExperts?.length || 0);
          // Don't touch hasLocalExperts in global mode — it's only relevant for location-specific views
        } else {
          // LOCATION MODE or SEARCH MODE: 3-tier priority ordering
          // Order by: verified first (membership_tier not null first), then global_rank
          // Reads the public-safe users_profiles view with an explicit column
          // list — a bare select('*') on users is denied by column grants.
          let query = db.raw
            .from('users_profiles')
            .select('id, name, lastname, display_name, business, image, marker_image, vanity_url, about, about_en, country, state_province, city, coordinates, location_id, global_rank, membership_tier, profile_verified, locale, created_at', { count: 'exact' })
            .eq('profile_active', true)
            .eq('profile_listed', true)
            .eq('has_email', true)
            .eq('has_valid_vanity', true);

          // Filter by location_id directly using effectiveLocation
          if (!isGlobalMode && effectiveLocation) {
            query = query.eq('location_id', effectiveLocation);
          }

          // Search by name, lastname, display_name, or business
          if (searchQuery.trim()) {
            query = query.or(`name.ilike.%${searchQuery}%,lastname.ilike.%${searchQuery}%,display_name.ilike.%${searchQuery}%,business.ilike.%${searchQuery}%`);
          }

          // Order by global_rank only — membership multiplier is already baked into rank
          const { data: locationExperts, error, count } = await query
            .order('global_rank', { ascending: true, nullsFirst: false })
            .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

          if (error) {
            logger.error('[Experts] Failed to load location/search experts', error);
            setLoadError(true);
            setExperts([]);
            setTotalCount(0);
          } else if ((locationExperts?.length || 0) === 0 && !isGlobalMode && effectiveLocation && !searchQuery.trim()) {
            // NO LOCAL EXPERTS: Fetch nearby experts from other locations
            setHasLocalExperts(false);
            
            // Get current location coordinates from database (using location_id)
            const { data: currentLocData } = await db.raw
              .from('locations')
              .select('coordinates, iso_alpha2')
              .eq('location_id', effectiveLocation)
              .maybeSingle();
            
            if (currentLocData?.coordinates) {
              const coords = currentLocData.coordinates.match(/-?\d+\.?\d*/g);
              if (coords && coords.length >= 2) {
                const currentLat = parseFloat(coords[0]);
                const currentLng = parseFloat(coords[1]);
                
                // Get all locations with coordinates and location_id
                const { data: allLocations } = await db.raw
                  .from('locations')
                  .select('location_id, iso_alpha2, coordinates, total_users_profile_listed')
                  .not('iso_alpha2', 'is', null)
                  .not('coordinates', 'is', null)
                  .gt('total_users_profile_listed', 0);
                
                // Calculate distances and sort by distance
                const locationsWithDistance = (allLocations || [])
                  .filter(l => l.iso_alpha2 !== currentLocData?.iso_alpha2)
                  .map(l => {
                    const locCoords = l.coordinates?.match(/-?\d+\.?\d*/g);
                    if (!locCoords || locCoords.length < 2) return null;
                    const lat = parseFloat(locCoords[0]);
                    const lng = parseFloat(locCoords[1]);
                    // Simple Euclidean distance (good enough for sorting)
                    const distance = Math.sqrt(Math.pow(lat - currentLat, 2) + Math.pow(lng - currentLng, 2));
                    return { ...l, distance };
                  })
                  .filter(Boolean)
                  .sort((a, b) => (a?.distance || 999) - (b?.distance || 999))
                  .slice(0, 20); // Top 20 nearest locations
                
            // Fetch top expert from each nearby location - use location_id directly
                const nearbyExperts: User[] = [];
                for (const loc of locationsWithDistance) {
                  if (!loc?.location_id) continue;
                  const nearbyExpert = await getTopExpertByLocation(db.raw, loc.location_id);
                  if (nearbyExpert) {
                    nearbyExperts.push(nearbyExpert as User);
                  }
                }
                
                setExperts(nearbyExperts);
                setTotalCount(nearbyExperts.length);
              } else {
                setExperts([]);
                setTotalCount(0);
              }
            } else {
              setExperts([]);
              setTotalCount(0);
            }
          } else {
            setExperts((locationExperts || []) as unknown as User[]);
            setTotalCount(count || 0);
            setHasLocalExperts((locationExperts?.length || 0) > 0);
          }
        }
      } catch {
        setExperts([]);
        setTotalCount(0);
      }
      
      setLoading(false);
      
      // Track search after results are loaded
      if (deferredSearchQuery && deferredSearchQuery !== searchTrackedRef.current) {
        trackSearchSubmit(deferredSearchQuery, totalCount, { source: 'experts' });
        searchTrackedRef.current = deferredSearchQuery;
      }
    };

    fetchExperts();
  }, [selectedLocations, selectedPracticeAreas, deferredSearchQuery, currentPage, currentLocation, intentMode, contextLoading, reloadKey]);

  // Batch fetch localized expert bios (metadata-first pattern)
  const expertIds = useMemo(() => experts.map(e => e.id), [experts]);
  const { getBioWithFallback } = useLocalizedExpertBios(expertIds);

  const getCountryInfo = (countryCode: string | null) => {
    if (!countryCode) return null;
    return countries.find(c => c.code === countryCode);
  };

  const getSelectedLocationsLabel = () => {
    if (selectedLocations.length === 0) return lCommon('labels.international', { defaultValue: 'International' });
    if (selectedLocations.length === 1) {
      const location = locations.find(l => l.id?.toString() === selectedLocations[0]);
      const displayName = location?.name || lCommon('labels.international', { defaultValue: 'International' });
      return displayName;
    }
    return lPages('experts.locations_selected', { count: selectedLocations.length, defaultValue: 'Locations Selected' });
  };

  const { getDisplayName: localizePracticeArea } = useLocalizedPracticeAreas();

  const getSelectedPracticeAreasLabel = () => {
    if (selectedPracticeAreas.length === 0) return lPages('experts.practice_areas', { defaultValue: 'Practice Areas' });
    if (selectedPracticeAreas.length === 1) {
      const area = practiceAreas.find(a => a.practice_area_id === selectedPracticeAreas[0]);
      return localizePracticeArea(area?.practice_area) || lPages('experts.one_selected', { defaultValue: 'One Selected' });
    }
    if (selectedPracticeAreas.length <= 2) {
      return selectedPracticeAreas
        .map(id => {
          const area = practiceAreas.find(a => a.practice_area_id === id);
          return area ? localizePracticeArea(area.practice_area) : null;
        })
        .filter(Boolean)
        .join(", ");
    }
    return lPages('experts.areas_selected', { count: selectedPracticeAreas.length, defaultValue: 'Areas Selected' });
  };

  const resetAllFilters = () => {
    // Clear local filters
    setSelectedLocations([]);
    setSelectedPracticeAreas([]);
    setSearchQuery('');
    setCurrentPage(1);
    
    // Update context to "International" (GLOBAL) - skip navigation on Experts page
    selectInternational({ skipNavigation: true });
  };

  const hasActiveFilters = selectedLocations.length > 0 || selectedPracticeAreas.length > 0 || searchQuery.trim() !== '';

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEOHead {...seoData} />
      <div className={cn("container", RESPONSIVE.pageContainerPy)}>
        <PageHeader
          title={<LPages id="experts.title" defaultValue='Experts' />}
          subtitle={<LPages id="experts.subtitle" defaultValue='Trusted advisors for your business needs' />}
          action={
            hasActiveFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetAllFilters}
                className="gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                <LPages id="experts.reset_filters" defaultValue="Reset Filters" />
              </Button>
            ) : undefined
          }
        />

        {/* Inquiry Suggestions */}
        {user && (
          <div className={cn(SPACING.marginBottom6)}>
            <InquirySuggestions compact maxSuggestions={1} />
          </div>
        )}

        {/* Filters - 3 Column Grid */}
        <div className={`${GRIDS.filterTrio} mb-6`}>
          <div className={FORM_CONTROLS.filterField.wrapper}>
            <label className={FORM_CONTROLS.filterField.label}>
              <LPages id="experts.search.label" defaultValue='Search' />
            </label>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={lPages('experts.search.placeholder', { defaultValue: 'Search experts' })}
              className="bg-background"
            />
          </div>

          <div className={FORM_CONTROLS.filterField.wrapper}>
            <label className={FORM_CONTROLS.filterField.label}>
              <LPages id="experts.locations" defaultValue='Locations' />
            </label>
            <LocationPicker
              value={selectedLocations.length > 0 ? selectedLocations[0] : INTERNATIONAL_LOCATION_ID}
              onSelect={(locationId) => {
                hasUserSelectedLocation.current = true;
                if (locationId === INTERNATIONAL_LOCATION_ID || locationId === 'GLOBAL' || locationId === null) {
                  setSelectedLocations([]);
                  selectInternational({ skipNavigation: true });
                } else {
                  setSelectedLocations([locationId]);
                  selectLocation(locationId, { skipNavigation: true });
                }
              }}
              showInternational={true}
              showAllLocations={false}
              variant="outline"
              size="default"
              align="start"
              triggerClassName={FORM_CONTROLS.filterField.trigger}
              className="w-[--radix-popover-trigger-width]"
            />
          </div>

          <div className={FORM_CONTROLS.filterField.wrapper}>
            <label className={FORM_CONTROLS.filterField.label}>
              <LPages id="experts.practice_areas" defaultValue='Practice Areas' />
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={FORM_CONTROLS.filterField.trigger}>
                  <span className="truncate">{getSelectedPracticeAreasLabel()}</span>
                  <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className={cn(
                SPACING.cardPadding,
                'w-[--radix-popover-trigger-width] bg-popover border-border z-50 max-h-[300px] overflow-y-auto'
              )} align="start">
                <div className={cn(SPACING.stackMd)}>
                  <div className="flex items-center space-x-2 pb-2 border-b">
                    <Checkbox
                      id="all-practice-areas"
                      checked={selectedPracticeAreas.length === 0}
                      onCheckedChange={(checked) => {
                        if (checked) setSelectedPracticeAreas([]);
                      }}
                    />
                    <label htmlFor="all-practice-areas" className={cn(TYPOGRAPHY.label, "cursor-pointer")}>
                      <LPages id="experts.all_practice_areas" defaultValue='All Practice Areas' />
                    </label>
                  </div>
                  {practiceAreas.map((area) => (
                    <div key={area.practice_area_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`area-${area.practice_area_id}`}
                        checked={selectedPracticeAreas.includes(area.practice_area_id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedPracticeAreas([...selectedPracticeAreas, area.practice_area_id]);
                          } else {
                            setSelectedPracticeAreas(selectedPracticeAreas.filter(a => a !== area.practice_area_id));
                          }
                        }}
                      />
                    <label htmlFor={`area-${area.practice_area_id}`} className="text-sm cursor-pointer">
                      {localizePracticeArea(area.practice_area)}
                    </label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* View Mode Tabs + CTA */}
        <div className={cn(SPACING.sectionHeaderLarge, "flex items-center justify-between gap-4")}>
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'list' | 'map')}>
            <TabsList className={`${TABS.listGrid2} max-w-[400px]`}>
              <TabsTrigger value="list" className={TABS.triggerGrid}>
                <List className={TABS.icon} />
                <span className={RESPONSIVE.tabletUpInline}><LPages id="experts.tabs.list_view" defaultValue='List View' /></span>
              </TabsTrigger>
              <TabsTrigger value="map" className={TABS.triggerGrid}>
                <MapIcon className={TABS.icon} />
                <span className={RESPONSIVE.tabletUpInline}><LPages id="experts.tabs.map_view" defaultValue='Map View' /></span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {(!hasPaidMembership || isSuperAdmin) && (
            <VerifyCTA
              variant={user ? 'upgrade' : 'signup'}
              placement="experts-page"
            />
          )}
        </div>

        {viewMode === 'map' ? (
          /* Map View - Lazy loaded */
          (<div className={cn(SPACING.sectionSeparator)}>
            <Suspense fallback={
              <div className="w-full h-[600px] bg-muted rounded-lg flex items-center justify-center">
                <Skeleton className="w-full h-full" />
              </div>
            }>
              <GlobalMap
                key={`experts-map-${mapKey}`}
                initialLocationCode={mapLocationCode}
              />
            </Suspense>
          </div>)
        ) : (
          /* List View - Experts Grid */
          (!loading && loadError ? (
            <Card role="alert">
              <CardContent className="py-10 flex flex-col items-center gap-4 text-center">
                <p className={TYPOGRAPHY.body}>
                  <LPages id="experts.load_error.message" defaultValue="We couldn't load experts right now." />
                </p>
                <Button variant="outline" onClick={() => setReloadKey((k) => k + 1)}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  <LCommon id="actions.retry" defaultValue="Retry" />
                </Button>
              </CardContent>
            </Card>
          ) : loading ? (<div className={GRIDS.featuredGrid}>
            {Array.from({ length: 6 }).map((_, i) => (
              <UserCardSkeleton key={i} />
            ))}
          </div>) : displayExperts.length === 0 ? (
          <p className="text-center text-muted-foreground py-12"><LPages id="experts.no_results.label" defaultValue='No experts found' /></p>
        ) : (
          <>
            {/* Soft disclaimer when no local experts found */}
            {currentLocation && currentLocation !== INTERNATIONAL_LOCATION_ID && intentMode !== 'international' && !hasLocalExperts && (
              <div className={cn(
                SPACING.sectionSeparator,
                SPACING.cardPadding,
                'bg-muted/30 border border-border/50 rounded-lg'
              )}>
                <p className={cn(TYPOGRAPHY.cardDescription, 'text-center')}>
                  <LPages
                    id="experts.no_local_experts_disclaimer.description"
                    values={{ location: (/^[A-Za-z]{2}$/.test(currentLocation || '') ? getCountryDisplayName(currentLocation, i18n.language) : null) || getCountryInfo(currentLocation)?.name || currentLocation }}
                    defaultValue='No local matches were found for your search criteria. Displaying global results instead.' />
                </p>
              </div>
            )}

            
            {/* Use virtualized grid for better performance with large lists */}
            {selectedLocations.length > 0 && selectedLocations[0] !== INTERNATIONAL_LOCATION_ID && selectedLocations[0] !== '0' && displayExperts.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 [grid-auto-rows:1fr]">
                  {displayExperts.slice(0, 2).map((expert) => (
                    <UserCardHorizontal key={expert.id} expert={expert} localizedBio={getBioWithFallback(expert.id, expert.about)} />
                  ))}
                </div>
                {displayExperts.length > 2 && (
                  <div className={GRIDS.featuredGrid}>
                    {displayExperts.slice(2).map((expert) => (
                      <UserCard key={expert.id} expert={expert} localizedBio={getBioWithFallback(expert.id, expert.about)} />
                    ))}
                  </div>
                )}
              </div>
            ) : USE_VIRTUAL_SCROLL && displayExperts.length > 30 ? (
              <VirtualizedUserGrid users={displayExperts} columns={3} localizedBiosMap={new Map(displayExperts.map(e => [e.id, getBioWithFallback(e.id, e.about)]))} />
            ) : (
              <div className={GRIDS.featuredGrid}>
                {displayExperts.map((expert) => {
                  const expertData = expert as User & { membership_tier?: string | null };
                  const isLocationSpecific = selectedLocations.length > 0 && selectedLocations[0] !== INTERNATIONAL_LOCATION_ID && selectedLocations[0] !== '0';
                  if (isLocationSpecific && isPremiumTier(expertData.membership_tier)) {
                    return (
                      <div key={expert.id} className="md:col-span-2">
                        <UserCardHorizontal expert={expertData} localizedBio={getBioWithFallback(expert.id, expert.about)} />
                      </div>
                    );
                  }
                  return <UserCard key={expert.id} expert={expert} localizedBio={getBioWithFallback(expert.id, expert.about)} />;
                })}
              </div>
            )}

          {/* Pagination Controls */}
          <div className="mt-8">
            <StandardPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCurrentPage}
              itemLabelKey="pagination.experts"
            />
          </div>
        </>
        ))
        )}
      </div>
    </>
  );
};

export default Experts;
